import { Component } from 'react';
import { debounceTime } from 'rxjs/operators';

import { Subscription } from 'rxjs';
import { IOC_TYPES } from '../../../../context/ioc.types';
import { lazyInject } from '../../../../context/module';
import { FormGroup } from '../../../../models/forms';
import { Logger } from '../../../../models/logger';
import { UploadInProgressFile } from '../../../../models/upload-in-progress-file';
import { AuthService } from '../../../../services/auth.service';
import { ProbedData } from '../../../../services/file-process.service';
import { UIContext } from '../../../../services/ui-context';
import { ConvertOptionCategories } from '../../../../types/dropdown.type';
import { InputFileInfo } from '../../../../types/file-upload.types';
import { OptionalChildrenProps } from '../../../../types/react.types';
import { OrientationUpdateEvent, ProcessorMetadata, RotationAndFlip, ToType } from '../../../../types/types';
import { cloneDeep, includes, isEqual } from '../../../../utils/lodash-min';
import { RxUtils } from '../../../../utils/rx-utils';
import { StringUtils } from '../../../../utils/string-utils';
import { untilDestroyed } from '../../../../utils/until-destroyed';
import { ConvertOptionsContext } from '../../../convert-options/convert-options-context';
import ProgressComponent from '../../progress/progress.component';
import Tooltip from '../../tooltip/tooltip.component';
import { FileInfoBoxComponent } from './file-info-box.component';
import styles from './image-display.component.module.scss';
import { ProcessorMetadataUtils } from '../../../../utils/processor-metadata.utils';
import { BrowserUtils } from '../../../../ui-utils/browser-utils';
import { withRequestIdleCallbackAsync, withRequestIdleCallback } from '../../../../annotations/request-idle-callback.annotation';

declare var dataURLtoBlob;
declare var loadImage;


export const IMAGE_DEFAULT_MARGIN = {

    formName: 'narrow-margin',
    formDisplayValue: 'Narrow',
    formValue: {
        left: 0.5,
        top: 0.5,
        right: 0.5,
        bottom: 0.5,
    },
    selected: false,
};

export const IMAGE_DEFAULT_PAGE_SIZE = {
    formName: 'a4',
    formDisplayValue: 'A4',
    formValue: {
        width: 8.27,
        height: 11.69,
    },
    selected: false,
};

export const IMAGE_DEFAULT_ALIGNMENT = {
    formName: 'center',
    formDisplayValue: 'Center',
    formValue: 'center',
    selected: true,
};

export const IMAGE_DEFAULT_PLACEMENT = {
    formName: 'cover',
    formDisplayValue: 'Cover',
    formValue: 'cover',
    selected: false,
};

export const IMAGE_DEFAULT_ORIENTATION = {
    formName: 'portrait',
    formDisplayValue: 'Portrait',
    formValue: 'portrait',
    selected: true,
};

export class Vertex {
    orientation: number;
    previous: Vertex;
    next: Vertex;
    flip: Vertex;

    constructor(orientation) {
        this.orientation = orientation;
    }

    setRelatives(previous, next, flip): void {
        this.previous = previous;
        this.next = next;
        this.flip = flip;
    }
}

// Remove when file_upload_manager.ts imported to this project
export type SupportedFileTypes = File | any;

interface ImageDisplayProps extends OptionalChildrenProps {
    convertOptionCategories: ConvertOptionCategories;
    convertOptionsContext: ConvertOptionsContext,
    file: any;
    uploadInProgressFile: UploadInProgressFile<SupportedFileTypes>;
    probedData: ProbedData;
    probing: boolean;
    toType: ToType | string;
    fromType: string[];
    inputFileInfo: InputFileInfo;
    removeFile: (data?: any) => void;
    orientationChanged: (data: OrientationUpdateEvent) => void;
    probe: (data: number) => void;
    onError?: () => void;
}

export class ImageDisplayComponent extends Component<ImageDisplayProps> {
    private readonly logger = Logger.getLogger('ImageDisplayComponent');
    static urlToImageCache = new Map<string, any>();
    static map = new Map<String, Vertex>();
    static mapLoaded = false;

    page: any;
    backgroundImg: any;
    backgroundSize: 'contain' | 'cover';
    backgroundPosition: 'top' | 'center' | 'bottom';
    currentOrientation = 1;
    originalOrientation = 1;

    rotationMap = new Map();
    flipMapH = new Map();
    flipMapV = new Map();

    toolsEnabled = false;

    count = 0;

    probeDurationOptionUUID = StringUtils.uuid();
    imageURL: any;
    sampleDurationFormGroup: FormGroup;

    imageBlobUrl: string;
    error = false;

    imageWidth = 0;
    imageHeight = 0;

    private containerHeight = 70;

    static initializeMap() {
        if (ImageDisplayComponent.mapLoaded) {
            return;
        }
        const one = new Vertex(1);
        const two = new Vertex(2);
        const three = new Vertex(3);
        const four = new Vertex(4);
        const five = new Vertex(5);
        const six = new Vertex(6);
        const seven = new Vertex(7);
        const eight = new Vertex(8);

        one.setRelatives(six, eight, two);
        two.setRelatives(five, seven, one);
        three.setRelatives(eight, six, four);
        four.setRelatives(seven, five, three);
        five.setRelatives(four, two, eight);
        six.setRelatives(three, one, seven);
        seven.setRelatives(two, four, six);
        eight.setRelatives(one, three, five);

        ImageDisplayComponent.map.set('1', one);
        ImageDisplayComponent.map.set('2', two);
        ImageDisplayComponent.map.set('3', three);
        ImageDisplayComponent.map.set('4', four);
        ImageDisplayComponent.map.set('5', five);
        ImageDisplayComponent.map.set('6', six);
        ImageDisplayComponent.map.set('7', seven);
        ImageDisplayComponent.map.set('8', eight);
        ImageDisplayComponent.mapLoaded = true;
    }

    static getRotationAndFlip(initialOrientation: string, finalOrientation: string): RotationAndFlip {
        const source: Vertex = ImageDisplayComponent.map.get(initialOrientation);
        let rotation = 360;
        if (initialOrientation === finalOrientation) {
            return {
                angle: '0',
                isFliped: 'false',
                orientation: finalOrientation,
            };
        }

        let i = 3;
        let temp: Vertex = source;
        while (i > 0) {
            rotation = rotation - 90;
            temp = temp.next;
            if ((temp.orientation + '') === finalOrientation) {
                return {
                    angle: rotation + '',
                    isFliped: 'false',
                    orientation: finalOrientation,
                };
            }
            i--;
        }

        temp = source.flip;
        rotation = 0;
        if ((temp.orientation + '') === finalOrientation) {
            return {
                angle: rotation + '',
                isFliped: 'true',
                orientation: finalOrientation,
            };
        }

        i = 3;
        while (i > 0) {
            rotation = rotation + 90;
            temp = temp.next;
            if ((temp.orientation + '') === finalOrientation) {
                return {
                    angle: rotation + '',
                    isFliped: 'true',
                    orientation: finalOrientation,
                };
            }
            i--;
        }

        return {
            angle: '0',
            isFliped: 'false',
            orientation: finalOrientation,
        };
    }

    @lazyInject(IOC_TYPES.AuthService) public auth: AuthService;
    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;

    private subs: Subscription[] = [];

    constructor(props) {
        super(props);
        ImageDisplayComponent.initializeMap();
        // https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images
        this.rotationMap.set(1, 8);
        this.rotationMap.set(8, 3);
        this.rotationMap.set(3, 6);
        this.rotationMap.set(6, 1);

        this.rotationMap.set(2, 5);
        this.rotationMap.set(5, 4);
        this.rotationMap.set(4, 7);
        this.rotationMap.set(7, 2);

        this.flipMapH.set(1, 2);
        this.flipMapH.set(2, 1);
        this.flipMapH.set(3, 4);
        this.flipMapH.set(4, 3);

        this.flipMapH.set(6, 5);
        this.flipMapH.set(5, 6);

        this.flipMapH.set(8, 7);
        this.flipMapH.set(7, 8);

        this.flipMapV.set(6, 7);
        this.flipMapV.set(2, 3);
        this.flipMapV.set(3, 2);
        this.flipMapV.set(7, 6);

        this.flipMapV.set(1, 4);
        this.flipMapV.set(4, 1);
        this.flipMapV.set(8, 5);
        this.flipMapV.set(5, 8);

        if (BrowserUtils.hasWindow()) {
            const optionChangedSub = this.props.convertOptionsContext.onSelectedOptionsChanged.pipe(
                untilDestroyed(this),
                debounceTime(100),
            ).subscribe(() => {
                this.convertOptionCategoriesChanged();
            });
            this.subs.push(optionChangedSub);

            const uploadInProgressFileChanged = this.props.uploadInProgressFile.changes.pipe(
                untilDestroyed(this),
                debounceTime(100),
            ).subscribe(() => {
                this.forceUpdate();
            });
            this.subs.push(uploadInProgressFileChanged);
        }
    }

    private orientaionToRotationAndFlip() {
        return ImageDisplayComponent.getRotationAndFlip(this.originalOrientation + '', this.currentOrientation + '');
    }

    componentDidMount() {
        if (!this.props.uploadInProgressFile.completed) {
            // completed is used as the key for this component. Thus, this makes multiple requests when completed state changes.
            return;
        }
        this.load()
            .then(() => this.refresh())
            .catch((e) => {
                this.logger.error(e);
                this.error = true;
            }).finally(() => {
                this.convertOptionCategoriesChanged();
                this.sampleDurationFormGroup = new FormGroup();
                this.sampleDurationFormGroup.setControl('sampleDuration');
                this.sampleDurationFormGroup.setValue({
                    sampleDuration: 40,
                });
            });
    }

    componentWillUnmount(): void {
        this.subs.forEach(sub => {
            RxUtils.safeCloseSubscriber(sub);
        })
    }

    @withRequestIdleCallback
    refresh() {
        const blobURL = ImageDisplayComponent.urlToImageCache.get(this.props.uploadInProgressFile.uuid);
        const image = loadImage(
            blobURL || this.imageURL,
            (canvas: HTMLCanvasElement, data) => {
                const t = dataURLtoBlob(canvas.toDataURL());
                const imageURL = URL.createObjectURL(t);
                this.logger.trace7({ message: `${this.currentOrientation}`, data: this.orientaionToRotationAndFlip() });
                const updatedInfo = {
                    file: this.props.file,
                    orientation: this.orientaionToRotationAndFlip(),
                };

                this.props.orientationChanged(updatedInfo);
                ImageDisplayComponent.urlToImageCache.set(this.props.uploadInProgressFile.uuid, this.imageURL);
                this.backgroundImg = 'url(' + imageURL + ')';
                setTimeout(() => this.forceUpdate(), 10);
            },
            {
                orientation: this.currentOrientation,
                canvas: true,
                maxWidth: 200,
            },
        );
    }

    async load(): Promise<any> {
        const readyPromise = this.props.uploadInProgressFile.whenResponseReady();
        await readyPromise;
        const processorMetadata = this.props.uploadInProgressFile.getMetadata().processorMetadataList[0];

        const token = this.props.uploadInProgressFile.getResponse().token;
        const gcName = this.props.uploadInProgressFile.getResponse().gcName || processorMetadata.fileMetadata.gcName;

        let url = ProcessorMetadataUtils.getThumbnailEndpoint(processorMetadata)
            + `/thumbnail?token=${token}&gcName=${gcName}` +
            `&uploadType=${processorMetadata.uploadType}&safeName=${processorMetadata.fileMetadata.safeName}`;

        const authToken = this.auth.getToken();
        if (authToken) {
            url = `${url}&authorization=${authToken}`;
        }


        return new Promise<void>((resolve, reject) => {
            const blobURL = ImageDisplayComponent.urlToImageCache.get(this.props.uploadInProgressFile.uuid);
            if (blobURL) {
                // This image is already loaded and this load request maybe due to react parent component force reload
                // resolve();
                // return;
            }
            const orientation = this.props.uploadInProgressFile.getOrientation();
            const orientationNumber = (orientation && orientation.orientation && (Number(orientation.orientation))) || undefined;
            loadImage(
                blobURL || url,
                (canvas: HTMLCanvasElement, data) => {
                    if (canvas && canvas.toDataURL) {
                        const t = dataURLtoBlob(canvas.toDataURL());
                        this.imageURL = URL.createObjectURL(t);
                        this.logger.trace7(`${this.currentOrientation}`);
                        this.currentOrientation = orientationNumber || (data.exif && data.exif.get('Orientation')) || 1;
                        if (!blobURL) {
                            // Only set this once
                            this.originalOrientation = this.currentOrientation;
                        }
                        if (!orientationNumber) {
                            // This component was load with a orientation. No  need to notify that
                            this.props.orientationChanged({
                                file: this.props.file,
                                orientation: this.orientaionToRotationAndFlip(),
                            });
                        }
                        this.backgroundImg = 'url(' + this.imageURL + ')';
                        this.imageWidth = canvas.width;
                        this.imageHeight = canvas.height;
                        ImageDisplayComponent.urlToImageCache.set(this.props.uploadInProgressFile.uuid, this.imageURL);
                        resolve();
                    } else {
                        reject();
                    }
                },
                {
                    // what stored on the map is in current orientation, doing this will re-orient it again
                    // ...(orientationNumber ? { orientation: orientationNumber } : undefined),
                    canvas: true,
                    meta: true,
                    maxWidth: 200,
                },
            );

        }).then(() => {
            this.toolsEnabled = true;
        }).catch((e) => {
            this.error = true;
        });
    }

    @withRequestIdleCallbackAsync
    async rotateFileFn(processRequest: boolean, e?) {
        e?.stopPropagation();
        if (!processRequest) {
            this.uiContext.errorToast(
                'Upload In Progress',
                'You can not rotate image until file upload is completed. Wait for a thumbnail to be shown.',
                false,
                false);
        }
        const nextOrientation = this.rotationMap.get(this.currentOrientation);
        this.currentOrientation = nextOrientation;
        this.refresh();
    }

    @withRequestIdleCallbackAsync
    async flipFileHFn(processRequest: boolean, e?) {
        e?.stopPropagation();
        if (!processRequest) {
            this.uiContext.errorToast(
                'Upload In Progress',
                'You can not filp image until file upload is completed. Wait for a thumbnail to be shown.',
                false,
                false);
        }
        const nextOrientation = this.flipMapH.get(this.currentOrientation);
        this.currentOrientation = nextOrientation;
        this.refresh();
    }

    @withRequestIdleCallbackAsync
    async flipFileVFn(processRequest: boolean, e?) {
        e?.stopPropagation();
        if (!processRequest) {
            this.uiContext.errorToast(
                'Upload In Progress',
                'You can not filp image until file upload is completed. Wait for a thumbnail to be shown.',
                false,
                false);
        }
        const nextOrientation = this.flipMapV.get(this.currentOrientation);
        this.currentOrientation = nextOrientation;
        this.refresh();
    }

    convertOptionCategoriesChanged() {
        // this.refresh();
        // create page rect
        const pageOptions = this.props.convertOptionCategories.formCategories.find(item => item.category === 'pageOptions');
        const page = {} as any;
        const pageSize: any = !pageOptions ? IMAGE_DEFAULT_PAGE_SIZE :
            pageOptions.formGroups.find(item => item.formGroupName === 'pageSize').selections.find(item => item.selected);
        const margin: any = !pageOptions ? IMAGE_DEFAULT_MARGIN :
            pageOptions.formGroups.find(item => item.formGroupName === 'margin').selections.find(item => item.selected);
        const isLandscape: boolean = !pageOptions ? true :
            pageOptions.formGroups.find(item => item.formGroupName === 'pageOrientation')
                .selections.find(item => item.selected).formName as any === 'landscape';

        const targetImageDimensions = {
            width: pageSize.formValue.width == -1 ? this.imageWidth : pageSize.formValue.width,
            height: pageSize.formValue.height == -1 ? this.imageHeight : pageSize.formValue.height,
        };

        const normalizer = this.containerHeight / Math.max(targetImageDimensions.width, targetImageDimensions.height);
        if (isLandscape) {
            page.size = {
                outer: {
                    x: 0,
                    y: 0,
                    width: targetImageDimensions.height * normalizer,
                    height: targetImageDimensions.width * normalizer,
                },
                inner: {
                    x: margin.formValue.left * normalizer,
                    y: margin.formValue.top * normalizer,
                    width: (targetImageDimensions.height - (margin.formValue.left + margin.formValue.right)) * normalizer,
                    height: (targetImageDimensions.width - (margin.formValue.top + margin.formValue.bottom)) * normalizer,
                },
            };
        } else {
            page.size = {
                outer: {
                    x: 0,
                    y: 0,
                    width: targetImageDimensions.width * normalizer,
                    height: targetImageDimensions.height * normalizer,
                },
                inner: {
                    x: margin.formValue.left * normalizer,
                    y: margin.formValue.top * normalizer,
                    width: (targetImageDimensions.width - (margin.formValue.left + margin.formValue.right)) * normalizer,
                    height: (targetImageDimensions.height - (margin.formValue.top + margin.formValue.bottom)) * normalizer,
                },
            };
        }

        const imageOptions = this.props.convertOptionCategories.formCategories.find(item => item.category === 'imageOptions');
        const imagePlacement: any = !imageOptions ? IMAGE_DEFAULT_PLACEMENT
            : imageOptions.formGroups.find(item => item.formGroupName === 'imagePlacement')
                .selections.find(item => item.selected);
        const imageAlignment: any = !imageOptions ? IMAGE_DEFAULT_ALIGNMENT
            : imageOptions.formGroups.find(item => item.formGroupName === 'imageAlignment')
                .selections.find(item => item.selected);

        const backgroundSize = imagePlacement.formName === 'cover' ? 'cover' : 'contain';
        const backgroundPosition = imageAlignment.formName;
        if (!this.isSame(this.page, page)
            || !this.isSame(this.backgroundSize, backgroundSize)
            || !this.isSame(this.backgroundPosition, backgroundPosition)) {
            this.page = page;
            this.backgroundSize = imagePlacement.formName === 'cover' ? 'cover' : 'contain';
            this.backgroundPosition = imageAlignment.formName;
            this.forceUpdate();
        }

    }

    isSame(a, b) {
        return isEqual(a, b);
    }

    // previousInputFileInfo = null;
    // shouldComponentUpdate(nextProps: ImageDisplayProps) {
    //     const hasChanged = !isEqual(nextProps.inputFileInfo, this.previousInputFileInfo?.inputFileInfo);
    //     this.previousInputFileInfo = cloneDeep(nextProps.inputFileInfo);
    //     return hasChanged;
    // }

    shouldShowRotateAndFlip() {
        if (this.props.toType === 'image' && includes(this.props.fromType, 'video')) {
            // Pallet(-lavfi) with -vf not supported yet
            return false;
        }
        return true;
    }

    render() {

        let fileInfoBox = <div className={styles['file-info-box']}>loading...</div>
        if (this.props.inputFileInfo) {
            fileInfoBox = <div className={`${styles['file-info-box']}`}>
                <FileInfoBoxComponent
                    fileInfo={this.props.inputFileInfo}
                    convertOptionsContext={this.props.convertOptionsContext}
                    toType={this.props.toType} fromType={this.props.fromType}></FileInfoBoxComponent>
            </div>
        }

        if (true) {
            return this.page && <div className={`${styles['page']}`}>
                <div className={styles['page-outer']} style={{ width: `${this.page.size.outer.width}px`, height: `${this.page.size.outer.height}px` }}>
                    <div className={styles['page-inner']} style={{
                        left: `${this.page.size.inner.x}px`, top: `${this.page.size.inner.y}px`,
                        width: `${this.page.size.inner.width}px`,
                        height: `${this.page.size.inner.height}px`,
                        backgroundImage: `${this.backgroundImg}`,
                        backgroundSize: `${this.backgroundSize}`,
                        backgroundPosition: `${this.backgroundPosition}`
                    }}
                    >
                        {
                            this.error && <div className={styles['error']}>
                                <img src="/icons/svg/generic-file.svg" />
                            </div>
                        }
                    </div>
                </div>
            </div>
        }

        return <div className={`${styles['card']} sort-handler`} key={this.props.uploadInProgressFile.uuid}>
            <div className={styles['card-body']}>
                <div className={`${styles['action-item-parent']} not-sort-handler`}>
                    <button className={`x-btn x-btn-primary x-btn-svg ${styles['action-item']}`} onClick={() => this.props.removeFile(this.props.file)}>
                        <img src="/icons/svg/x.svg" />
                    </button>
                    {
                        !this.error && this.shouldShowRotateAndFlip() && <button className={`x-btn x-btn-gray x-btn-gray-outline x-btn-svg ${styles['action-item']}`}
                            onClick={() => this.rotateFileFn(this.toolsEnabled)}>
                            <img src="/icons/svg/arrow-counterclockwise.svg" />
                        </button>
                    }
                    {
                        !this.error && this.shouldShowRotateAndFlip() && <button className={`x-btn x-btn-gray x-btn-gray-outline x-btn-svg ${styles['action-item']}`}
                            onClick={() => this.flipFileHFn(this.toolsEnabled)}
                        >
                            <img src="/icons/svg/arrow-left-right.svg" />
                        </button>
                    }
                    {
                        !this.error && this.shouldShowRotateAndFlip() && <button className={`x-btn x-btn-gray x-btn-gray-outline x-btn-svg ${styles['action-item']}`}
                            onClick={() => this.flipFileVFn(this.toolsEnabled)}>
                            <img src="/icons/svg/arrow-down-up.svg" />
                        </button>
                    }
                </div>
                {
                    this.page && <div className={`${styles['page']}`}>
                        <div className={styles['page-outer']} style={{ width: `${this.page.size.outer.width}px`, height: `${this.page.size.outer.height}px` }}>
                            <div className={styles['page-inner']} style={{
                                left: `${this.page.size.inner.x}px`, top: `${this.page.size.inner.y}px`,
                                width: `${this.page.size.inner.width}px`,
                                height: `${this.page.size.inner.height}px`,
                                backgroundImage: `${this.backgroundImg}`,
                                backgroundSize: `${this.backgroundSize}`,
                                backgroundPosition: `${this.backgroundPosition}`
                            }}
                            >
                                {
                                    this.error && <div>
                                        Preview Not Available
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }

            </div>
            <div className={`${styles['title']} ${styles['ellipsis']}`}>
                <Tooltip text={this.props.file.name}>
                    {this.props.file.name}
                </Tooltip>
            </div>
            <div className={styles['file-upload-progress']}>
                <ProgressComponent
                    value={this.props.uploadInProgressFile.getProgress()}
                    postFix='%'
                    preFix={this.props.uploadInProgressFile.getProgress() === 100 ? 'Uploaded' : 'Uploading:'}>
                </ProgressComponent>
            </div>
            {
                fileInfoBox
            }
        </div>
    }
}
